import React, {useEffect} from 'react';
import {t} from 'i18next';
import {
    useCreateTimeTrialMutation,
    useFetchTimeTrialSummaryQuery,
} from '@api';
import Rank from '@components/common/RankText';
import {TimeTrialProgressView} from '@components/TimeTrialProgress';
import {useSelector} from '@utils/hooks/common';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {
    faArrowRight,
    faRankingStar,
} from '@fortawesome/free-solid-svg-icons';
import {ThemeAwareSkeleton} from '@components/theme/ThemeAwareSkeleton';
import {Link, useNavigate} from 'react-router';

const TimeTrial: React.FC = () => {
    const navigate = useNavigate();
    const {data: summary, isLoading, isError} =
        useFetchTimeTrialSummaryQuery(undefined, {
            refetchOnMountOrArgChange: true,
        });
    const timeTrials = summary?.featured;

    const [createTimeTrial, {data: result}] = useCreateTimeTrialMutation();
    const {user} = useSelector(state => state.auth);

    useEffect(() => {
        if (result && 'id' in result) {
            navigate(`/time-trials/${result.id}`);
        }
    }, [navigate, result]);

    return (
        <div className="index-home-feature index-page time-trial h-full w-full min-w-0 flex-1 overflow-hidden">
            <div className="index-home-section-heading">
                <h2 className="index-home-heading-title">
                    <span
                        className="index-home-heading-icon index-home-heading-brand-icon index-home-heading-brand-icon--time-trial"
                        aria-hidden="true"
                    />
                    <span>{t('index.time_trial.title')}</span>
                </h2>
                <div className="index-home-heading-actions">
                    <Link
                        to="/time-trials/leaderboard"
                        className="index-home-header-icon-link"
                        title={t('index.leaderboard')}
                        aria-label={`${t('index.time_trial.title')} ${t('index.leaderboard')}`}>
                        <FontAwesomeIcon icon={faRankingStar} />
                    </Link>
                    <span>
                        <Link
                            to="/time-trials"
                            aria-label={`${t('index.see_all')} ${t('index.time_trial.title')}`}
                            className="index-home-section-link">
                            {t('index.see_all')}{' '}
                            <FontAwesomeIcon icon={faArrowRight} />
                        </Link>
                    </span>
                </div>
            </div>
            <div className="card index-home-feature-card flex-1">
                <div className="card-body p-0">
                    <div className="list-group list-group-flush time-trials-list">
                        <table className="table table-responsive time-trials-list-table-index time-trial-table">
                            <thead>
                                <tr>
                                    <th>{t('index.level')}</th>
                                    <th>{t('index.best')}</th>
                                    <th>
                                        <span className="sr-only">
                                            {t('common.action')}
                                        </span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {isLoading
                                    ? Array.from({length: 3}, (_, index) => (
                                          <tr
                                              key={`skeleton-${index}`}
                                              className="align-middle">
                                              <td>
                                                  <ThemeAwareSkeleton
                                                      width={50}
                                                  />
                                              </td>
                                              <td>
                                                  <ThemeAwareSkeleton
                                                      width={100}
                                                  />
                                              </td>
                                              <td>
                                                  <ThemeAwareSkeleton
                                                      width={80}
                                                  />
                                              </td>
                                          </tr>
                                      ))
                                    : timeTrials?.map(timeTrial => {
                                          const rowClass =
                                              'align-middle table-entry' +
                                              (timeTrial.victoriesCount &&
                                              timeTrial.victoriesCount > 0
                                                  ? ' table-success'
                                                  : '');

                                          return (
                                              <tr
                                                  key={timeTrial.level}
                                                  className={rowClass}>
                                                  <td>
                                                      <Rank
                                                          rank={timeTrial.rank}
                                                      />
                                                  </td>
                                                  <td>
                                                      {timeTrial.bestScore ==
                                                          null ||
                                                      timeTrial.bestScore ===
                                                          0 ? (
                                                          <span>-</span>
                                                      ) : (
                                                          timeTrial.bestScore
                                                      )}
                                                  </td>
                                                  <td className="text-right">
                                                      <div className="float-end">
                                                          <button
                                                              onClick={() =>
                                                                  createTimeTrial(
                                                                      {
                                                                          level: timeTrial.level,
                                                                      }
                                                                  )
                                                              }
                                                              className="btn btn-sm btn-secondary application-time-trial-start index-home-table-action">
                                                              {t('index.start')}
                                                          </button>
                                                      </div>
                                                  </td>
                                              </tr>
                                          );
                                      })}
                            </tbody>
                        </table>
                    </div>
                    {user && (
                        <div className="index-home-time-trial-progress">
                            <TimeTrialProgressView
                                data={summary?.progress}
                                isLoading={isLoading}
                                isError={isError}
                                variant="home"
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TimeTrial;
